import {Component, DestroyRef, ElementRef, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ActivatedRoute, Router} from "@angular/router";
import {combineLatest} from "rxjs";
import {NotificationService} from "../service/Notification.service";
import {SortDirectionValuesModel} from "../models/SortDirectionValues-model";
import {WinOlsStatusFilter, WinOlsTransactionService} from "../service/winols-transaction.service";

@Component({
    selector: 'app-win-ols-transaction',
    templateUrl: './win-ols-transaction.component.html',
    styleUrl: './win-ols-transaction.component.css'
})
export class WinOlsTransactionComponent implements OnInit {
    readonly winOlsTransactionService = inject(WinOlsTransactionService);
    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly notificationService = inject(NotificationService);
    private readonly destroyRef = inject(DestroyRef);

    transactionId: number | null = null;
    selectedStatus: WinOlsStatusFilter | null = null;
    @ViewChild('rangeDialog') private rangeDialog?: ElementRef<HTMLDialogElement>;
    @ViewChild('rangeButton') private rangeButton?: ElementRef<HTMLButtonElement>;
    isDateRangePickerOpened = false;
    draftStart = '';
    draftEnd = '';
    draftRangeError = '';
    readonly defaultDateRange = this.todayRange();

    private readonly allowedStatuses: WinOlsStatusFilter[] = [
        'SOLUTION_OFFERED', 'COMPLETED', 'PRE_CHECK', 'REQUESTED', 'ERROR_OR_NO_OPTIONS'
    ];

    ngOnInit(): void {
        this.winOlsTransactionService.resetTableHeaders();
        this.winOlsTransactionService.getAllTableHeaderNames();

        combineLatest([this.route.paramMap, this.route.queryParamMap])
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([params, query]) => {
                const transactionId = params.get('transactionId');
                if (transactionId) {
                    this.transactionId = Number(transactionId);
                    this.selectedStatus = null;
                    this.winOlsTransactionService.clearDateRange();
                    this.winOlsTransactionService.getTransactionById(this.transactionId);
                    return;
                }

                this.transactionId = null;
                const startDate = query.get('startDate');
                const endDate = query.get('endDate');
                const status = query.get('status');
                if (startDate && endDate && this.isValidRange(startDate, endDate)) {
                    if (status && this.allowedStatuses.includes(status as WinOlsStatusFilter)) {
                        this.selectedStatus = status as WinOlsStatusFilter;
                        this.winOlsTransactionService.getWinOlsDataRangeByStatus(startDate, endDate, this.selectedStatus);
                    } else {
                        this.selectedStatus = null;
                        this.winOlsTransactionService.setDateRange(startDate, endDate);
                    }
                } else {
                    this.selectedStatus = null;
                    const today = this.todayRange();
                    this.winOlsTransactionService.setDateRange(today.startDate, today.endDate);
                }
            });
    }

    get maxDateTime(): string {
        return this.localDateTime(new Date());
    }

    openDateRangePicker(): void {
        const currentRange = this.winOlsTransactionService.selectedDateRange();
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(0, 0, 0, 0);
        this.draftStart = currentRange?.startDate.slice(0, 16) ?? this.localDateTime(midnight);
        this.draftEnd = currentRange?.endDate.slice(0, 16) ?? this.localDateTime(now);
        this.draftRangeError = '';
        const dialog = this.rangeDialog?.nativeElement;
        if (!dialog || dialog.open) return;
        const button = this.rangeButton?.nativeElement.getBoundingClientRect();
        dialog.style.top = Math.max(16, Math.min((button?.bottom || 60) + 10, window.innerHeight - 360)) + 'px';
        dialog.showModal();
        this.isDateRangePickerOpened = true;
    }

    closeDateRangePicker(): void {
        this.rangeDialog?.nativeElement.close();
        this.onDateRangePickerClosed();
    }

    onDateRangePickerClosed(): void {
        this.isDateRangePickerOpened = false;
        this.draftRangeError = '';
    }

    onDateRangeBackdropClick(event: MouseEvent): void {
        const dialog = this.rangeDialog?.nativeElement;
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right ||
            event.clientY < rect.top || event.clientY > rect.bottom) this.closeDateRangePicker();
    }

    applyDateRange(): void {
        if (!this.draftStart || !this.draftEnd) {
            this.draftRangeError = 'Choose both a start and end date.';
            return;
        }
        if (!this.isValidRange(this.draftStart, this.draftEnd)) {
            this.draftRangeError = 'End date must be on or after start date.';
            return;
        }
        if (new Date(this.draftEnd).getTime() > Date.now()) {
            this.draftRangeError = 'Choose a date and time that is not in the future.';
            return;
        }
        this.onDateRangeSelected({startDate: this.draftStart, endDate: this.draftEnd});
        this.closeDateRangePicker();
    }

    selectToday(): void {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(0, 0, 0, 0);
        this.draftStart = this.localDateTime(midnight);
        this.draftEnd = this.localDateTime(now);
        this.applyDateRange();
    }

    private todayRange(): {startDate: string; endDate: string} {
        const now = new Date();
        const midnight = new Date(now);
        midnight.setHours(0, 0, 0, 0);
        return {startDate: this.localDateTime(midnight) + ':00', endDate: this.localDateTime(now) + ':00'};
    }

    private localDateTime(date: Date): string {
        const pad = (value: number) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    onDateRangeSelected(value: {startDate: string; endDate: string}): void {
        if (!this.isValidRange(value.startDate, value.endDate)) {
            this.notificationService.showErrorMessage('End date must be on or after start date.', 'Invalid date range');
            return;
        }

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                startDate: this.withSeconds(value.startDate),
                endDate: this.withSeconds(value.endDate)
            },
            queryParamsHandling: '',
            replaceUrl: true
        });
    }

    get selectedStatusLabel(): string {
        const labels: Record<WinOlsStatusFilter, string> = {
            SOLUTION_OFFERED: 'Solution Offered',
            COMPLETED: 'Completed',
            PRE_CHECK: 'Precheck',
            REQUESTED: 'Requested',
            ERROR_OR_NO_OPTIONS: 'Error / No Options'
        };
        return this.selectedStatus ? labels[this.selectedStatus] : '';
    }

    clearStatusFilter(): void {
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {status: null},
            queryParamsHandling: 'merge'
        });
    }

    onRefreshTable(): void {
        if (this.isDateRangePickerOpened) this.closeDateRangePicker();
        if (this.transactionId !== null) {
            this.winOlsTransactionService.getTransactionById(this.transactionId);
        } else if (this.route.snapshot.queryParamMap.keys.length > 0) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {},
                queryParamsHandling: '',
                replaceUrl: true
            });
        } else {
            const today = this.todayRange();
            this.winOlsTransactionService.setDateRange(today.startDate, today.endDate);
        }
    }

    isTableEmpty(): boolean {
        return this.winOlsTransactionService.winOlsTransactions().length === 0;
    }

    downloadFileFromTable(event: {trace: string; id: number; route: string}): void {
        this.winOlsTransactionService.downloadFileFromTable(event.trace, event.id, event.route);
    }

    changeSortDirection(sortDirection: SortDirectionValuesModel): void {
        this.winOlsTransactionService.changeSortDirection(sortDirection);
    }

    private isValidRange(startDate: string, endDate: string): boolean {
        const start = new Date(startDate).getTime();
        const end = new Date(endDate).getTime();
        return Number.isFinite(start) && Number.isFinite(end) && start <= end;
    }

    private withSeconds(value: string): string {
        return value.length === 16 ? `${value}:00` : value;
    }
}
